import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react'; // Add this import
import { Helmet } from "react-helmet";
// ─── SEO Component ──────────────────────────────────────────────────────────────
function SEOHead() {
  useEffect(() => {
    // Page Title
    document.title = "Login - DonatePE";
    
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    
    // Meta Description
    setMeta("description", "Login to your DonatePE account to manage fundraisers, track donations, and access your crowdfunding dashboard.");
    
    // Robots - noindex for private page
    setMeta("robots", "noindex, nofollow");
    
    // Charset
    let charset = document.querySelector('meta[charset="UTF-8"]');
    if (!charset) {
      charset = document.createElement("meta");
      charset.setAttribute("charset", "UTF-8");
      document.head.insertBefore(charset, document.head.firstChild);
    }
    
    // Viewport
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      viewport.setAttribute("content", "width=device-width, initial-scale=1.0");
      document.head.appendChild(viewport);
    }
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://donatepe.org/login");
    
    // Open Graph Tags
    setMeta("og:title", "Login - DonatePE", true);
    setMeta("og:description", "Login to your DonatePE account to manage fundraisers, track donations, and access your crowdfunding dashboard.", true);
    setMeta("og:url", "https://donatepe.org/login", true);
    setMeta("og:type", "website", true);
    setMeta("og:image", "https://donatepe.org/logo.png", true);
    
    // Twitter Card Tags
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Login - DonatePE");
    setMeta("twitter:description", "Login to your DonatePE account to manage fundraisers, track donations, and access your crowdfunding dashboard.");
    setMeta("twitter:image", "https://donatepe.org/logo.png");
  }, []);
  
  return null;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    mobile: '',
    password: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [currentFeature, setCurrentFeature] = useState(0);
  const [showPassword, setShowPassword] = useState(false); // Single state for password visibility
  
  // Captcha States
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [captchaExpired, setCaptchaExpired] = useState(false);
  const [captchaAttempts, setCaptchaAttempts] = useState(0);
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  
  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    mobile: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
    otp_txn_id: ''
  });
  const [forgotPasswordErrors, setForgotPasswordErrors] = useState({});
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Replace with your actual Google reCAPTCHA v2 site key
  const RECAPTCHA_SITE_KEY = "6LduoWIsAAAAAK32BSNyk9alDXL6xSkFAkKHEeQx"; // Google test key

  const features = [
    { 
      title: "Secure Login", 
      description: "Protected by reCAPTCHA v2 for enhanced security", 
      gradient: "from-green-500 to-emerald-600",
      icon: "🛡️"
    },
    { 
      title: "Zero Platform Fees", 
      description: "100% of donations go to your cause", 
      gradient: "from-teal-500 to-teal-600",
      icon: "💰"
    },
    { 
      title: "Quick Access", 
      description: "Get back to managing your fundraiser instantly", 
      gradient: "from-purple-500 to-indigo-600",
      icon: "⚡"
    }
  ];

  // Load reCAPTCHA v2 script
  useEffect(() => {
    if (scriptLoadedRef.current) return;
    
    const loadCaptchaScript = () => {
      return new Promise((resolve, reject) => {
        if (window.grecaptcha) {
          scriptLoadedRef.current = true;
          setCaptchaLoaded(true);
          resolve();
          return;
        }

        const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
        if (existingScript) {
          const checkInterval = setInterval(() => {
            if (window.grecaptcha) {
              clearInterval(checkInterval);
              scriptLoadedRef.current = true;
              setCaptchaLoaded(true);
              resolve();
            }
          }, 300);

          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("reCAPTCHA load timeout"));
          }, 10000);

          return;
        }

        window.recaptchaCallback = function () {
          setCaptchaLoaded(true);
          scriptLoadedRef.current = true;
          resolve();
        };

        const script = document.createElement("script");
        script.src = "https://www.google.com/recaptcha/api.js?onload=recaptchaCallback&render=explicit";
        script.async = true;
        script.defer = true;

        script.onerror = () => {
          reject(new Error("Failed to load reCAPTCHA"));
        };

        document.head.appendChild(script);
      });
    };

    const initCaptcha = async () => {
      try {
        await loadCaptchaScript();
        setTimeout(() => {
          if (captchaRef.current && widgetIdRef.current === null) {
            renderCaptcha();
          }
        }, 500);
      } catch (error) {
        
      }
    };

    initCaptcha();

    return () => {
      delete window.recaptchaCallback;
    };
  }, []);

  // Render captcha function
  const renderCaptcha = () => {
    if (!window.grecaptcha || !captchaRef.current) {
      return;
    }

    if (widgetIdRef.current !== null) {
      return;
    }

    try {
      const widgetId = window.grecaptcha.render(captchaRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: handleCaptchaVerify,
        "expired-callback": handleCaptchaExpired,
        "error-callback": handleCaptchaError,
        theme: "light",
        size: "normal",
      });

      widgetIdRef.current = widgetId;
      setCaptchaExpired(false);
      setCaptchaError("");
      setCaptchaAttempts(0);
    } catch (error) {
      
      setCaptchaError("Failed to render captcha. Please refresh the page.");
    }
  };

  // Re-render captcha when container is ready
  useEffect(() => {
    if (captchaLoaded && window.grecaptcha && captchaRef.current && !widgetIdRef.current) {
      const timer = setTimeout(() => {
        renderCaptcha();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [captchaLoaded]);

  // Callback when reCAPTCHA v2 is verified
  const handleCaptchaVerify = (response) => {
   
    setCaptchaToken(response);
    setCaptchaVerified(true);
    setCaptchaError('');
    setCaptchaExpired(false);
  };

  // Callback when reCAPTCHA v2 expires
  const handleCaptchaExpired = () => {
    
    setCaptchaVerified(false);
    setCaptchaToken('');
    setCaptchaExpired(true);
    setCaptchaError('reCAPTCHA expired. Please check the box again.');
  };

  // Callback when reCAPTCHA v2 errors
  const handleCaptchaError = () => {
    
    setCaptchaError('reCAPTCHA verification failed. Please try again.');
  };

  // Reset captcha
  const resetCaptcha = () => {
    if (window.grecaptcha && widgetIdRef.current !== null) {
      try {
        window.grecaptcha.reset(widgetIdRef.current);
        setCaptchaVerified(false);
        setCaptchaToken('');
        setCaptchaExpired(false);
    
      } catch (error) {
       
        if (captchaRef.current) {
          widgetIdRef.current = null;
          setTimeout(() => renderCaptcha(), 500);
        }
      }
    }
  };

  // Manual retry function
  const handleRetryCaptcha = () => {
    setCaptchaError('');
    setCaptchaVerified(false);
    setCaptchaToken('');
    
    if (widgetIdRef.current) {
      resetCaptcha();
    } else {
      renderCaptcha();
    }
  };

  // Validation functions
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'mobile':
        if (!value.trim()) error = 'Mobile number is required';
        else if (!/^\d{10}$/.test(value)) error = 'Please enter a valid 10-digit mobile number';
        break;
      case 'password':
        if (!value.trim()) error = 'Password is required';
        break;
      default:
        break;
    }
    return error;
  };

  const validateForgotPasswordField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'mobile':
        if (!value.trim()) error = 'Mobile number is required';
        else if (!/^\d{10}$/.test(value)) error = 'Please enter a valid 10-digit mobile number';
        break;
      case 'otp':
        if (!value.trim()) error = 'OTP is required';
        else if (!/^\d{6}$/.test(value)) error = 'Please enter a valid 6-digit OTP';
        break;
      case 'newPassword':
        if (!value.trim()) error = 'New password is required';
        else if (value.length < 8) error = 'Password must be at least 8 characters';
        else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) 
          error = 'Password must contain uppercase, lowercase and number';
        break;
      case 'confirmPassword':
        if (!value.trim()) error = 'Please confirm your password';
        else if (value !== forgotPasswordData.newPassword) error = 'Passwords do not match';
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'mobile') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setForgotPasswordData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setForgotPasswordData(prev => ({ ...prev, [name]: value }));
    }
    
    if (forgotPasswordErrors[name]) {
      setForgotPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleForgotPasswordBlur = (field) => {
    const error = validateForgotPasswordField(field, forgotPasswordData[field]);
    setForgotPasswordErrors(prev => ({ ...prev, [field]: error }));
  };

  // Send OTP for Forgot Password
  const handleSendOTP = async (e) => {
    e.preventDefault();

    const mobileError = validateForgotPasswordField('mobile', forgotPasswordData.mobile);
    
    if (mobileError) {
      setForgotPasswordErrors({ mobile: mobileError });
      return;
    }

    try {
      setForgotPasswordLoading(true);
      setForgotPasswordErrors({});
      setCaptchaError('');

      const payload = {
        mobile: forgotPasswordData.mobile
      };

      const response = await fetch("https://api.donatepe.org/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setForgotPasswordData(prev => ({
          ...prev,
          otp_txn_id: data.otp_txn_id
        }));
        setForgotPasswordStep(2);
        setResendTimer(60);
      } else {
        setForgotPasswordErrors({ 
          mobile: data.message || "Mobile number not registered" 
        });
      }

    } catch (error) {
      setForgotPasswordErrors({ 
        mobile: "Failed to send OTP. Please try again." 
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    try {
      setForgotPasswordLoading(true);
      setCaptchaError('');

      const payload = {
        mobile: forgotPasswordData.mobile
      };

      const response = await fetch("https://api.donatepe.org/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setForgotPasswordData(prev => ({
          ...prev,
          otp_txn_id: data.otp_txn_id
        }));
        setResendTimer(60);
        setForgotPasswordErrors({});
      } else {
        setForgotPasswordErrors({ 
          otp: data.message || "Failed to resend OTP" 
        });
      }

    } catch (error) {
      setForgotPasswordErrors({ 
        otp: "Failed to resend OTP. Please try again." 
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    const otpError = validateForgotPasswordField('otp', forgotPasswordData.otp);
    const newPasswordError = validateForgotPasswordField('newPassword', forgotPasswordData.newPassword);
    const confirmPasswordError = validateForgotPasswordField('confirmPassword', forgotPasswordData.confirmPassword);

    if (otpError || newPasswordError || confirmPasswordError) {
      setForgotPasswordErrors({
        otp: otpError,
        newPassword: newPasswordError,
        confirmPassword: confirmPasswordError
      });
      return;
    }

    try {
      setForgotPasswordLoading(true);
      setCaptchaError('');

      const payload = {
        mobile: forgotPasswordData.mobile,
        otp: forgotPasswordData.otp,
        otp_txn_id: forgotPasswordData.otp_txn_id,
        newPassword: forgotPasswordData.newPassword
      };

      const response = await fetch("https://api.donatepe.org/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        alert("Password reset successful! Please login with your new password.");
        
        setFormData(prev => ({
          ...prev,
          mobile: forgotPasswordData.mobile
        }));
        
        setShowForgotPassword(false);
        setForgotPasswordStep(1);
        setForgotPasswordData({
          mobile: '',
          otp: '',
          newPassword: '',
          confirmPassword: '',
          otp_txn_id: ''
        });
        setForgotPasswordErrors({});
      } else {
        setForgotPasswordErrors({ 
          otp: data.message || "Failed to reset password. Please try again." 
        });
      }

    } catch (error) {
      setForgotPasswordErrors({ 
        otp: "Failed to reset password. Please try again." 
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      mobile: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
      otp_txn_id: ''
    });
    setForgotPasswordErrors({});
  };

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();

    const mobileError = validateField('mobile', formData.mobile);
    const passwordError = validateField('password', formData.password);

    if (mobileError || passwordError) {
      setFieldErrors({
        mobile: mobileError,
        password: passwordError
      });
      setTouchedFields({
        mobile: true,
        password: true
      });
      return;
    }

    // Check if captcha is verified
    if (!captchaVerified || !captchaToken) {
      setCaptchaError('Please verify that you are not a robot');
      return;
    }

    try {
      setIsLoading(true);
      setApiError('');
      setCaptchaError('');

      const payload = {
        mobile: formData.mobile,
        password: formData.password,
        captchaToken: captchaToken
      };

      const response = await fetch("https://api.donatepe.org/auth/login", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        setApiError(`Server error (${response.status}). Please try again.`);
        resetCaptcha();
        return;
      }

      if (response.ok && data.success) {
        
sessionStorage.setItem("user", JSON.stringify(data.user));
        navigate('/');
      } else {
        if (data.message) {
          setApiError(data.message);
        } else if (response.status === 401) {
          setApiError("Invalid mobile number or password. Please try again.");
        } else {
          setApiError(`Login failed (${response.status}). Please try again.`);
        }
        
        resetCaptcha();
      }

    } catch (error) {
      setApiError("Login failed. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewAccount = () => {
    navigate('/start-fundraiser');
  };

  return (
    <>

    

<Helmet>
  <title>DonatePe | Raise Funds for Medical & Social Causes in India</title>

  <meta
    name="description"
    content="DonatePe is a trusted crowdfunding platform in India to raise funds for medical emergencies, personal needs, and social causes. Start fundraising or donate easily."
  />

  <meta
    name="keywords"
    content="donate, crowdfunding India, medical fundraiser, charity, donatepe"
  />

  {/* Open Graph */}
  <meta property="og:title" content="DonatePe | Online Fundraising Platform" />
  <meta
    property="og:description"
    content="Raise funds for causes you care about or support others through DonatePe."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://donatepe.org/" />

  {/* Recommended additions */}
  <meta property="og:image" content="https://donatepe.org/preview.png" />
  <link rel="canonical" href="https://donatepe.org/" />
</Helmet>
      <SEOHead />
      {/* Breadcrumb Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://donatepe.org"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Login",
              "item": "https://donatepe.org/login"
            }
          ]
        })}
      </script>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50">
      {/* Loading Overlay */}
      {(isLoading || forgotPasswordLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-700">
              {forgotPasswordLoading ? 'Processing...' : 'Logging in...'}
            </span>
          </div>
        </div>
      )}
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-white">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                  <span className="text-lg text-white">🔑</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Reset Password
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {forgotPasswordStep === 1 ? 'Enter your mobile number' : 'Enter OTP and new password'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseForgotPassword}
                className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5">
              {forgotPasswordStep === 1 ? (
                // Step 1: Mobile Number
                <form onSubmit={handleSendOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
                      <input
                        type="tel"
                        name="mobile"
                        value={forgotPasswordData.mobile}
                        onChange={handleForgotPasswordChange}
                        onBlur={() => handleForgotPasswordBlur('mobile')}
                        placeholder="9876543210"
                        maxLength="10"
                        className={`
                          w-full pl-12 pr-3.5 py-3 rounded-lg border-2 transition-all duration-300
                          focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                          placeholder:text-gray-400 text-gray-900 text-sm
                          ${forgotPasswordErrors.mobile 
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      />
                    </div>
                    {forgotPasswordErrors.mobile && (
                      <p className="mt-1 text-xs text-red-600 text-left">
                        {forgotPasswordErrors.mobile}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 pt-5 border-t border-gray-100">
                    <div className="space-y-3">
                      <button
                        type="submit"
                        disabled={forgotPasswordLoading}
                        className={`
                          w-full px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                          ${forgotPasswordLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:shadow-md hover:shadow-teal-500/25'
                          }
                          flex items-center justify-center
                        `}
                      >
                        {forgotPasswordLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Sending OTP...
                          </>
                        ) : 'Send OTP'}
                      </button>

                      <div className="relative flex items-center justify-center">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <span className="flex-shrink-0 px-3 text-xs text-gray-500">OR</span>
                        <div className="flex-grow border-t border-gray-200"></div>
                      </div>

                      <button
                        type="button"
                        onClick={handleCloseForgotPassword}
                        className="w-full px-6 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                // Step 2: OTP and New Password
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                      Enter OTP <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="otp"
                      value={forgotPasswordData.otp}
                      onChange={handleForgotPasswordChange}
                      onBlur={() => handleForgotPasswordBlur('otp')}
                      placeholder="6-digit OTP"
                      maxLength="6"
                      className={`
                        w-full px-3.5 py-3 rounded-lg border-2 transition-all duration-300
                        focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                        placeholder:text-gray-400 text-gray-900 text-sm
                        ${forgotPasswordErrors.otp 
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                          : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                    />
                    {forgotPasswordErrors.otp && (
                      <p className="mt-1 text-xs text-red-600 text-left">
                        {forgotPasswordErrors.otp}
                      </p>
                    )}
                    
                    <div className="mt-2 flex justify-between items-center">
                      <p className="text-xs text-gray-500">
                        Didn't receive OTP?
                      </p>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={resendTimer > 0 || forgotPasswordLoading}
                        className="text-xs font-semibold text-teal-600 hover:text-teal-700 disabled:text-gray-400 disabled:cursor-not-allowed transition"
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </div>

                  {/* New Password Field with Eye Icon */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        name="newPassword"
                        value={forgotPasswordData.newPassword}
                        onChange={handleForgotPasswordChange}
                        onBlur={() => handleForgotPasswordBlur('newPassword')}
                        placeholder="Enter new password"
                        className={`
                          w-full px-3.5 py-3 pr-12 rounded-lg border-2 transition-all duration-300
                          focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                          placeholder:text-gray-400 text-gray-900 text-sm
                          ${forgotPasswordErrors.newPassword 
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-teal-600 transition"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {forgotPasswordErrors.newPassword && (
                      <p className="mt-1 text-xs text-red-600 text-left">
                        {forgotPasswordErrors.newPassword}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field with Eye Icon */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={forgotPasswordData.confirmPassword}
                        onChange={handleForgotPasswordChange}
                        onBlur={() => handleForgotPasswordBlur('confirmPassword')}
                        placeholder="Confirm new password"
                        className={`
                          w-full px-3.5 py-3 pr-12 rounded-lg border-2 transition-all duration-300
                          focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                          placeholder:text-gray-400 text-gray-900 text-sm
                          ${forgotPasswordErrors.confirmPassword 
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-teal-600 transition"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {forgotPasswordErrors.confirmPassword && (
                      <p className="mt-1 text-xs text-red-600 text-left">
                        {forgotPasswordErrors.confirmPassword}
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg text-left">
                    Password must be at least 8 characters with uppercase, lowercase and numbers.
                  </p>

                  <div className="mt-4 pt-5 border-t border-gray-100">
                    <div className="space-y-3">
                      <button
                        type="submit"
                        disabled={forgotPasswordLoading}
                        className={`
                          w-full px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                          ${forgotPasswordLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:shadow-md hover:shadow-teal-500/25'
                          }
                          flex items-center justify-center
                        `}
                      >
                        {forgotPasswordLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Resetting...
                          </>
                        ) : 'Reset Password'}
                      </button>

                      <div className="relative flex items-center justify-center">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <span className="flex-shrink-0 px-3 text-xs text-gray-500">OR</span>
                        <div className="flex-grow border-t border-gray-200"></div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setForgotPasswordStep(1)}
                        className="w-full px-6 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="relative min-h-screen flex">
        {/* Left Section */}
        <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-teal-50 via-white to-white">
          <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
            <div className="max-w-lg">
              <div className="mb-12 transition-all duration-1000">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl mb-8 shadow-2xl shadow-teal-500/20">
                  <span className="text-3xl text-white">📱</span>
                </div>
                
                <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Mobile <span className="text-teal-600">Login</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Secure access with mobile number and reCAPTCHA v2 protection.
                </p>
              </div>
              
              <div className="transition-all duration-1000 delay-300">
                <div className="space-y-5">
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className={`p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
                        ${currentFeature === index ? 'border-teal-300 shadow-xl shadow-teal-500/10 scale-[1.02]' : 
                          'border-gray-100 shadow-sm hover:border-gray-200'
                        }
                      `}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <span className="text-2xl">{feature.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-lg mb-1.5">{feature.title}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center Content */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-6 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            <div className="text-center mb-10 w-full">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                Login to Your Account
              </h2>
              <p className="text-gray-600 mt-3 text-lg">
                Enter your mobile number and password to continue
              </p>
            </div>
            
            <div className="w-full flex justify-center">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <span className="text-lg text-white">📱</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Login Details
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Enter your mobile number and password
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-5">
                  {apiError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{apiError}</p>
                    </div>
                  )}

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Mobile Number Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
                          <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            onBlur={() => handleBlur('mobile')}
                            placeholder="9876543210"
                            maxLength="10"
                            className={`
                              w-full pl-12 pr-3.5 py-3 rounded-lg border-2 transition-all duration-300
                              focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                              placeholder:text-gray-400 text-gray-900 text-sm
                              ${fieldErrors.mobile && touchedFields.mobile 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                          />
                        </div>
                        {fieldErrors.mobile && touchedFields.mobile && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.mobile}
                          </p>
                        )}
                      </div>

                      {/* Password Field with Eye Icon - SINGLE IMPLEMENTATION */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onBlur={() => handleBlur('password')}
                            placeholder="Enter your password"
                            className={`
                              w-full px-3.5 py-3 pr-12 rounded-lg border-2 transition-all duration-300
                              focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500
                              placeholder:text-gray-400 text-gray-900 text-sm
                              ${fieldErrors.password && touchedFields.password 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                          />
                          {/* Eye Icon Button */}
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-teal-600 transition"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {fieldErrors.password && touchedFields.password && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.password}
                          </p>
                        )}
                        <div className="mt-2 text-right">
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-sm font-semibold text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                          >
                            Forgot Password?
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* reCAPTCHA v2 Container */}
                    <div className="mt-6 flex flex-col items-center">
                      {!captchaLoaded && (
                        <div className="text-center py-4">
                          <div className="inline-block w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-sm text-gray-600 mt-2">Loading captcha...</p>
                        </div>
                      )}
                      
                      <div 
                        ref={captchaRef}
                        className="g-recaptcha"
                      ></div>

                      {captchaExpired && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg w-full">
                          <p className="text-xs text-yellow-600 text-center">
                            reCAPTCHA expired. Please check the box again.
                          </p>
                        </div>
                      )}

                      {captchaError && !captchaExpired && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg w-full">
                          <p className="text-xs text-red-600 text-center">
                            {captchaError}
                          </p>
                          <button
                            onClick={handleRetryCaptcha}
                            className="mt-2 text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full hover:bg-red-200 transition-colors"
                          >
                            Retry
                          </button>
                        </div>
                      )}

                      {/* reCAPTCHA Legal Text */}
                      <p className="text-xs text-gray-500 mt-3 text-center">
                        This site is protected by reCAPTCHA and the Google 
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline mx-1">Privacy Policy</a> 
                        and 
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline mx-1">Terms of Service</a> 
                        apply.
                      </p>
                    </div>

                    <div className="mt-4 pt-5 border-t border-gray-100">
                      <div className="space-y-3">
                        <button
                          type="submit"
                          disabled={isLoading || !captchaVerified}
                          className={`
                            w-full px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                            ${isLoading || !captchaVerified
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:shadow-md hover:shadow-teal-500/25'
                            }
                            flex items-center justify-center
                          `}
                        >
                          {isLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Logging in...
                            </>
                          ) : 'Login'}
                        </button>

                        <div className="relative flex items-center justify-center">
                          <div className="flex-grow border-t border-gray-200"></div>
                          <span className="flex-shrink-0 px-3 text-xs text-gray-500">OR</span>
                          <div className="flex-grow border-t border-gray-200"></div>
                        </div>

                        <button
                          type="button"
                          onClick={handleCreateNewAccount}
                          className="w-full px-6 py-2.5 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
                        >
                          Create New Account
                        </button>
                      </div>
                    </div>
                  </form>

                  <div className="text-center pt-4 mt-4 border-t border-gray-100">
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600">
                        Don't have an account?{' '}
                        <a
                          href="/start-fundraiser"
                          className="font-semibold text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                        >
                          Start a fundraiser
                        </a>
                      </p>
                      <p className="text-xs text-gray-600">
                        Having trouble?{' '}
                        <a
                          href="/contact"
                          className="font-semibold text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                        >
                          Contact support
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8 w-full">
              <p className="text-xs text-gray-500">
                By logging in, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LoginPage;